import React, { useState, useMemo, useEffect } from 'react';
import { useApp } from '../../context/SupabaseAppContext';
import { Idea } from '../../types';
import {
  PlusIcon,
  Squares2X2Icon,
  TableCellsIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowsUpDownIcon,
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  CalendarIcon,
  StarIcon,
  ChatBubbleLeftIcon,
  TrashIcon,
  SparklesIcon,
  ClockIcon,
  UserIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon
} from '@heroicons/react/24/outline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import AttachmentList from '../Attachments/AttachmentList';
import CommentForm from '../Comments/CommentForm';
import CommentList from '../Comments/CommentList';
import Pagination from '../Common/Pagination';
import { analyzeIdeaStandard, analyzeIdeaDeepResearch, getMockAnalysis, formatAnalysisText } from '../../lib/aiAnalysis';
import { uploadAttachment } from '../../lib/attachments';
import { toast } from 'react-toastify';
import { getURLState, updateURLState, clearURLState, onURLStateChange } from '../../lib/urlState';
import { 
  saveAnalysisSession, 
  updateAnalysisSession, 
  clearAnalysisSession, 
  getRecoveryData,
  isLocalStorageAvailable
} from '../../lib/sessionPersistence';

interface IdeasManagementProps {
  onShowIdeaForm: () => void;
  onEditIdea?: (idea: Idea) => void;
}

type ViewMode = 'table' | 'kanban';
type SortField = 'title' | 'status' | 'category' | 'owner' | 'score' | 'dateSubmitted';
type SortDirection = 'asc' | 'desc';

const IdeasManagement: React.FC<IdeasManagementProps> = ({ onShowIdeaForm, onEditIdea }) => {
  const { state } = useApp();
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [showLinkedTasks, setShowLinkedTasks] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('dateSubmitted');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  // AI Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisType, setAnalysisType] = useState<'standard' | 'deep' | null>(null);
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [isSavingAnalysis, setIsSavingAnalysis] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentRefreshTrigger, setCommentRefreshTrigger] = useState(0);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [isRestoringState, setIsRestoringState] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  // Panel width state
  const [isPanelWide, setIsPanelWide] = useState(false);

  // Initialize state from URL and localStorage on component mount
  useEffect(() => {
    const initializeState = () => {
      const urlState = getURLState();
      // Restore selected idea from URL
      if (urlState.selectedIdeaId) {
        const idea = state.ideas.find(i => i.id === urlState.selectedIdeaId);
        if (idea) {
          setIsRestoringState(true);
          setSelectedIdea(idea);
          // Check for analysis session recovery
          if (isLocalStorageAvailable()) {
            const recoveryData = getRecoveryData(idea.id);
            if (recoveryData) {
              setIsAnalyzing(recoveryData.isAnalyzing);
              setAnalysisType(recoveryData.analysisType);
              setAnalysisResult(recoveryData.analysisResult || null);
              setAnalysisError(recoveryData.analysisError || null);
              
              // Show recovery notification
              if (recoveryData.isAnalyzing) {
                toast.info(`🔄 Resuming AI analysis for "${idea.title}"...`, {
                  autoClose: 3000
                });
              } else if (recoveryData.analysisResult) {
                toast.success(`✅ Restored AI analysis results for "${idea.title}"`, {
                  autoClose: 3000
                });
              }
            }
          }
          
          // Clear restoring state after a brief delay
          setTimeout(() => setIsRestoringState(false), 1000);
        } else {
          clearURLState(['selectedIdeaId', 'analysisState', 'analysisType']);
        }
      } else {
        }
    };

    // Always try to initialize, even with no ideas (for debugging)
    initializeState();
    
    // Also initialize when ideas are loaded
    if (state.ideas.length > 0) {
      setTimeout(initializeState, 100); // Small delay to ensure state is fully updated
    }
  }, [state.ideas]);

  // Listen for URL state changes (back/forward navigation)
  useEffect(() => {
    const unsubscribe = onURLStateChange((urlState) => {
      if (urlState.selectedIdeaId) {
        const idea = state.ideas.find(i => i.id === urlState.selectedIdeaId);
        if (idea && idea.id !== selectedIdea?.id) {
          setSelectedIdea(idea);
        }
      } else if (selectedIdea) {
        setSelectedIdea(null);
      }
    });

    return unsubscribe;
  }, [state.ideas, selectedIdea]);

  // Handle tab visibility changes to restore state when returning to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && state.ideas.length > 0) {
        const urlState = getURLState();
        
        if (urlState.selectedIdeaId && !selectedIdea) {
          const idea = state.ideas.find(i => i.id === urlState.selectedIdeaId);
          if (idea) {
            setIsRestoringState(true);
            setSelectedIdea(idea);
            
            // Check for analysis session recovery
            if (isLocalStorageAvailable()) {
              const recoveryData = getRecoveryData(idea.id);
              if (recoveryData) {
                setIsAnalyzing(recoveryData.isAnalyzing);
                setAnalysisType(recoveryData.analysisType);
                setAnalysisResult(recoveryData.analysisResult || null);
                setAnalysisError(recoveryData.analysisError || null);
                
                // Show recovery notification
                if (recoveryData.isAnalyzing) {
                  toast.info(`🔄 Resuming AI analysis for "${idea.title}"...`, {
                    autoClose: 3000
                  });
                } else if (recoveryData.analysisResult) {
                  toast.success(`✅ Restored AI analysis results for "${idea.title}"`, {
                    autoClose: 3000
                  });
                }
              }
            }
            
            // Clear restoring state after a brief delay
            setTimeout(() => setIsRestoringState(false), 1000);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [state.ideas, selectedIdea]);

  // Helper functions - defined before useMemo hooks
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New': return 'bg-blue-500';
      case 'Under Review': return 'bg-yellow-500';
      case 'Approved': return 'bg-green-500';
      case 'In Progress': return 'bg-purple-500';
      case 'Completed': return 'bg-emerald-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'Under Review': return 'bg-yellow-100 text-yellow-800';
      case 'Approved': return 'bg-green-100 text-green-800';
      case 'In Progress': return 'bg-purple-100 text-purple-800';
      case 'Completed': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryBadgeColor = (category: string) => {
    switch (category) {
      case 'Feature': return 'badge-primary';
      case 'Customer Request': return 'bg-cyan-100 text-cyan-800';
      case 'Product': return 'badge-success';
      case 'Process': return 'bg-orange-100 text-orange-800';
      case 'Technology': return 'bg-purple-100 text-purple-800';
      case 'Sales and Marketing': return 'bg-pink-100 text-pink-800';
      case 'Innovation': return 'badge-info';
      default: return 'badge-info';
    }
  };

  // Standard AI Analysis function (faster)
  const handleStandardAnalysis = async (idea: Idea) => {
    if (!idea) return;
    
    setIsAnalyzing(true);
    setAnalysisType('standard');
    setAnalysisError(null);
    setAnalysisResult(null);

    // Persist analysis state to localStorage and URL
    if (isLocalStorageAvailable()) {
      saveAnalysisSession({
        ideaId: idea.id,
        analysisType: 'standard',
        isAnalyzing: true,
        startTime: Date.now(),
        lastUpdate: Date.now()
      });
    }
    updateURLState({ analysisState: 'analyzing', analysisType: 'standard' });

    try {
      const analysis = await analyzeIdeaStandard(idea);
      setAnalysisResult(analysis);
      // Update session with results
      if (isLocalStorageAvailable()) {
        updateAnalysisSession({
          isAnalyzing: false,
          analysisResult: analysis
        });
      }
      updateURLState({ analysisState: 'completed' });
      
    } catch (error) {
      console.error('Standard AI analysis error:', error);
      
      // If real AI fails, fall back to mock analysis
      const mockAnalysis = getMockAnalysis(idea);
      setAnalysisResult(mockAnalysis);
      
      // Set error message but still show the mock analysis
      let errorMessage: string;
      if (error instanceof Error) {
        errorMessage = `AI service unavailable: ${error.message}. Showing mock analysis instead.`;
      } else {
        errorMessage = 'AI service unavailable. Showing mock analysis instead.';
      }
      setAnalysisError(errorMessage);
      
      // Update session with error and mock result
      if (isLocalStorageAvailable()) {
        updateAnalysisSession({
          isAnalyzing: false,
          analysisResult: mockAnalysis,
          analysisError: errorMessage
        });
      }
      updateURLState({ analysisState: 'completed' });
      
    } finally {
      setIsAnalyzing(false);
      // Don't clear analysisType - keep it for popup header display
    }
  };

  // Deep Research Analysis function (comprehensive with web search)
  const handleDeepResearch = async (idea: Idea) => {
    if (!idea) return;
    
    setIsAnalyzing(true);
    setAnalysisType('deep');
    setAnalysisError(null);
    setAnalysisResult(null);

    // Persist analysis state to localStorage and URL
    if (isLocalStorageAvailable()) {
      saveAnalysisSession({
        ideaId: idea.id,
        analysisType: 'deep',
        isAnalyzing: true,
        startTime: Date.now(),
        lastUpdate: Date.now()
      });
    }
    updateURLState({ analysisState: 'analyzing', analysisType: 'deep' });

    try {
      const analysis = await analyzeIdeaDeepResearch(idea);
      setAnalysisResult(analysis);
      // Update session with results
      if (isLocalStorageAvailable()) {
        updateAnalysisSession({
          isAnalyzing: false,
          analysisResult: analysis
        });
      }
      updateURLState({ analysisState: 'completed' });
      
    } catch (error) {
      console.error('Deep research analysis error:', error);
      
      // If real AI fails, fall back to mock analysis
      const mockAnalysis = getMockAnalysis(idea);
      setAnalysisResult(mockAnalysis);
      
      // Set error message but still show the mock analysis
      let errorMessage: string;
      if (error instanceof Error) {
        errorMessage = `AI service unavailable: ${error.message}. Showing mock analysis instead.`;
      } else {
        errorMessage = 'AI service unavailable. Showing mock analysis instead.';
      }
      setAnalysisError(errorMessage);
      
      // Update session with error and mock result
      if (isLocalStorageAvailable()) {
        updateAnalysisSession({
          isAnalyzing: false,
          analysisResult: mockAnalysis,
          analysisError: errorMessage
        });
      }
      updateURLState({ analysisState: 'completed' });
      
    } finally {
      setIsAnalyzing(false);
      // Don't clear analysisType - keep it for popup header display
    }
  };

  const handleSaveAnalysis = async () => {
    if (!selectedIdea || !analysisResult) return;
    
    setIsSavingAnalysis(true);
    
    try {
      // Determine analysis type for filename and content
      const analysisTypeText = analysisType === 'standard' ? 'Standard A.I. Analysis' : 'Deep Research Analysis';
      
      // Create a text file with the analysis
      const analysisText = `${analysisTypeText} for: ${selectedIdea.title}\n\nGenerated on: ${new Date().toLocaleString()}\n\n${analysisResult}`;
      const blob = new Blob([analysisText], { type: 'text/plain' });
      const filePrefix = analysisType === 'standard' ? 'AI_Analysis' : 'Deep_Research';
      const file = new File([blob], `${filePrefix}_${selectedIdea.title.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.txt`, { type: 'text/plain' });
      
      // Upload as attachment
      await uploadAttachment({ file, ideaId: selectedIdea.id });
      
      toast.success('🤖 Analysis saved as attachment successfully!');
      
      // Refresh attachments by triggering a re-render
      setCommentRefreshTrigger(prev => prev + 1);
      
    } catch (error) {
      console.error('Failed to save analysis:', error);
      toast.error('❌ Failed to save analysis as attachment');
    } finally {
      setIsSavingAnalysis(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleIdeaClick = (idea: Idea) => {
    setSelectedIdea(idea);
    // Reset AI analysis state when selecting a new idea
    setAnalysisResult(null);
    setAnalysisError(null);
    setIsAnalyzing(false);
    setAnalysisType(null);
    setIsSavingAnalysis(false);
    
    // Persist selection to URL
    updateURLState({ selectedIdeaId: idea.id });
    // Verify URL state was set correctly
    setTimeout(() => {
      const urlState = getURLState();
      }, 100);
  };

  const closePanel = () => {
    setSelectedIdea(null);
    setShowLinkedTasks(false);
    setShowCommentForm(false);
    setAnalysisResult(null);
    setAnalysisError(null);
    setIsAnalyzing(false);
    setAnalysisType(null);
    setIsSavingAnalysis(false);
    setIsAnalysisModalOpen(false);
    setIsPanelWide(false); // Reset panel width when closing

    // Clear URL state and localStorage sessions
    clearURLState(['selectedIdeaId', 'analysisState', 'analysisType']);
    clearAnalysisSession();
    };

  // Get linked tasks for the selected idea
  const getLinkedTasks = (ideaId: string) => {
    return state.tasks.filter(task => task.linkedIdeaId === ideaId);
  };

  // Handle View Tasks button click
  const handleViewTasks = () => {
    setShowLinkedTasks(true);
  };

  // Handle Edit Idea button click
  const handleEditIdea = () => {
    if (selectedIdea && onEditIdea) {
      onEditIdea(selectedIdea);
      // Don't close the panel - let the edit form handle navigation
      // The URL state will be preserved for when the user returns
    }
  };

  // Check if current user can edit the selected idea
  const canEditIdea = (idea: Idea) => {
    // TEMPORARY: Allow all users to edit any idea
    return true;
    
    // Original permission check (commented out for now):
    // return state.currentUser && idea.owner.id === state.currentUser.id;
  };

  // Comment handlers
  const handleAddComment = () => {
    setShowCommentForm(true);
  };

  const handleCommentAdded = () => {
    setShowCommentForm(false);
    setCommentRefreshTrigger(prev => prev + 1);
  };

  const handleCancelComment = () => {
    setShowCommentForm(false);
  };

  // Get task status colors
  const getTaskStatusColor = (status: string) => {
    switch (status) {
      case 'To Do': return 'bg-blue-100 text-blue-800';
      case 'In Progress': return 'bg-purple-100 text-purple-800';
      case 'Done': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTaskPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Handle Escape key to close panel or modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isAnalysisModalOpen) {
          setIsAnalysisModalOpen(false);
        } else if (selectedIdea) {
          closePanel();
        }
      }
    };

    if (selectedIdea || isAnalysisModalOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [selectedIdea, isAnalysisModalOpen]);

  // Debug: Log selectedIdea changes
  React.useEffect(() => {
    }, [selectedIdea]);

  // Debug: Log ideas array
  React.useEffect(() => {
    }, [state.ideas]);

  // Filter and sort ideas
  const filteredIdeas = useMemo(() => {
    let filtered = state.ideas;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(idea =>
        idea.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        idea.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        idea.uniqueId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        idea.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(idea => idea.status === statusFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(idea => idea.category === categoryFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'category':
          aValue = a.category;
          bValue = b.category;
          break;
        case 'owner':
          aValue = a.owner.name.toLowerCase();
          bValue = b.owner.name.toLowerCase();
          break;
        case 'score':
          aValue = a.score || 0;
          bValue = b.score || 0;
          break;
        case 'dateSubmitted':
          aValue = new Date(a.dateSubmitted).getTime();
          bValue = new Date(b.dateSubmitted).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [state.ideas, searchQuery, statusFilter, categoryFilter, sortField, sortDirection]);

  // Pagination logic
  const totalFilteredIdeas = filteredIdeas.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedIdeas = filteredIdeas.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, categoryFilter, sortField, sortDirection]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Group ideas by status for Kanban view (use all filtered ideas for kanban)
  const kanbanGroups = useMemo(() => {
    const statuses = ['New', 'Under Review', 'Approved', 'In Progress', 'Completed'];
    return statuses.map(status => ({
      status,
      ideas: filteredIdeas.filter(idea => idea.status === status),
      color: getStatusColor(status)
    }));
  }, [filteredIdeas]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <FontAwesomeIcon icon={faLightbulb} className="mr-3 text-deep-teal" />
            Ideas Management
          </h1>
          <p className="text-gray-600 mt-2">
            Create, Track, and Prioritize Ideas for Kajeet
          </p>
        </div>
        <div className="mt-4 lg:mt-0 flex items-center space-x-4">
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'table'
                  ? 'bg-white text-deep-teal shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <TableCellsIcon className="h-4 w-4 mr-2" />
              Table
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'kanban'
                  ? 'bg-white text-deep-teal shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Squares2X2Icon className="h-4 w-4 mr-2" />
              Kanban
            </button>
          </div>
          
          <button 
            onClick={onShowIdeaForm}
            className="btn-aqua flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            New Idea
          </button>
        </div>
      </div>

      {/* State Restoration Indicator */}
      {isRestoringState && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
            <span className="text-blue-700 text-sm">🔄 Restoring your session...</span>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
        {/* Search */}
        <div className="flex-1 max-w-md">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search ideas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-10"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field min-w-32"
            >
              <option value="all">All Statuses</option>
              <option value="New">New</option>
              <option value="Under Review">Under Review</option>
              <option value="Approved">Approved</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
            </select>
          </div>
          
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="input-field min-w-0"
          >
            <option value="all">All Categories</option>
            <option value="Feature">Feature</option>
            <option value="Product">Product</option>
            <option value="Process">Process</option>
            <option value="Technology">Technology</option>
            <option value="Sales and Marketing">Sales and Marketing</option>
            <option value="Innovation">Innovation</option>
          </select>
        </div>
      </div>

      {/* Main Content - Full Width */}
      <div className="w-full">
        {viewMode === 'table' ? (
          /* Table View */
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      { key: 'title', label: 'Title' },
                      { key: 'status', label: 'Status' },
                      { key: 'category', label: 'Category' },
                      { key: 'owner', label: 'Owner' },
                      { key: 'score', label: 'Score' },
                      { key: 'dateSubmitted', label: 'Submitted' },
                    ].map(({ key, label }) => (
                      <th
                        key={key}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort(key as SortField)}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{label}</span>
                          <ArrowsUpDownIcon className="h-4 w-4" />
                        </div>
                      </th>
                    ))}
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedIdeas.map((idea) => (
                    <tr
                      key={idea.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleIdeaClick(idea)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`flex-shrink-0 h-2 w-2 rounded-full mr-3 ${getStatusColor(idea.status)}`}></div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <div className="text-sm font-medium text-gray-900">{idea.title}</div>
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full font-mono">
                                {idea.uniqueId}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500 truncate max-w-xs">{idea.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(idea.status)}`}>
                          {idea.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`badge ${getCategoryBadgeColor(idea.category)}`}>
                          {idea.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm mr-2">{idea.owner.avatar}</span>
                          <span className="text-sm text-gray-900">{idea.owner.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                          <span className="text-sm font-medium text-gray-900">
                            {idea.score?.toFixed(1) || '0.0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(idea.dateSubmitted), 'MMM d, yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleIdeaClick(idea);
                          }}
                          className="text-deep-teal hover:text-deep-teal/80 mr-3"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
                            {totalFilteredIdeas === 0 && (
              <div className="text-center py-12">
                <FontAwesomeIcon icon={faLightbulb} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No ideas found</h3>
                <p className="text-gray-500 mb-4">
                  {searchQuery || statusFilter !== 'all' || categoryFilter !== 'all'
                    ? 'Try adjusting your filters or search query.'
                    : 'Get started by creating your first idea.'}
                </p>
                <button onClick={onShowIdeaForm} className="btn-aqua">
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create First Idea
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Kanban View */
          <div className="overflow-x-auto">
            <div className="flex space-x-4 min-w-max pb-4">
              {kanbanGroups.map(({ status, ideas, color }) => (
                <div key={status} className="flex-shrink-0 w-72">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className={`px-4 py-3 rounded-t-lg ${color} text-white`}>
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium">{status}</h3>
                        <span className="bg-white bg-opacity-20 text-xs px-2 py-1 rounded-full">
                          {ideas.length}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 space-y-4">
                      {ideas.map((idea) => (
                        <div
                          key={idea.id}
                          className="bg-gray-50 rounded-lg p-4 cursor-pointer hover:bg-gray-100 transition-colors border border-gray-200"
                          onClick={() => handleIdeaClick(idea)}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-900 line-clamp-2">
                              {idea.title}
                            </h4>
                            <span className={`badge text-xs ml-2 ${getCategoryBadgeColor(idea.category)}`}>
                              {idea.category}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 line-clamp-2 mb-3">
                            {idea.description}
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span className="text-xs">{idea.owner.avatar}</span>
                              <span className="text-xs text-gray-500">{idea.owner.name}</span>
                            </div>
                            <div className="flex items-center">
                              <StarIcon className="h-3 w-3 text-yellow-400 mr-1" />
                              <span className="text-xs font-medium text-gray-700">
                                {idea.score?.toFixed(1) || '0.0'}
                              </span>
                            </div>
                          </div>
                          {idea.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {idea.tags.slice(0, 3).map((tag) => (
                                <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-deep-teal bg-opacity-10 text-deep-teal">
                                  {tag}
                                </span>
                              ))}
                              {idea.tags.length > 3 && (
                                <span className="text-xs text-gray-500">+{idea.tags.length - 3} more</span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                      {ideas.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <FontAwesomeIcon icon={faLightbulb} className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No ideas in {status.toLowerCase()}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pagination for Table View */}
      {viewMode === 'table' && totalFilteredIdeas > 0 && (
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalItems={totalFilteredIdeas}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemName="ideas"
          />
        </div>
      )}

      {/* Slide-over Panel */}
      {selectedIdea && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity z-40"
            onClick={closePanel}
          />
          
          {/* Slide-over Panel */}
          <div className={`fixed right-0 top-0 h-full w-full bg-white shadow-xl transform transition-all duration-300 ease-in-out z-50 overflow-y-auto ${
            isPanelWide ? 'max-w-4xl' : 'max-w-md'
          }`}>
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="bg-deep-teal px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Idea Details</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setIsPanelWide(!isPanelWide)}
                    className="text-white hover:text-gray-200 transition-colors p-1 rounded hover:bg-white/10"
                    title={isPanelWide ? "Make panel narrower" : "Make panel wider"}
                  >
                    {isPanelWide ? (
                      <ArrowsPointingInIcon className="h-5 w-5" />
                    ) : (
                      <ArrowsPointingOutIcon className="h-5 w-5" />
                    )}
                  </button>
                  <button
                    onClick={closePanel}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
              
              {/* Content */}
              <div className="flex-1 px-6 py-6">
                <div className="flex items-center space-x-3 mb-6">
                  <h4 className="text-2xl font-bold text-gray-900">{selectedIdea.title}</h4>
                  <span className="text-sm px-2 py-1 bg-gray-100 text-gray-600 rounded-full font-mono">
                    {selectedIdea.uniqueId}
                  </span>
                </div>
                
                <div className="space-y-6">
                  {/* 2-Column Layout for Status, Tags, Category, and Submitted */}
                  <div className="grid grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-6">
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">Status</h5>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(selectedIdea.status)}`}>
                          {selectedIdea.status}
                        </span>
                      </div>

                      {selectedIdea.tags.length > 0 && (
                        <div>
                          <h5 className="text-sm font-semibold text-gray-700 mb-3">Tags</h5>
                          <div className="flex flex-wrap gap-2">
                            {selectedIdea.tags.map((tag) => (
                              <span key={tag} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-deep-teal bg-opacity-10 text-deep-teal">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">Category</h5>
                        <span className={`badge ${getCategoryBadgeColor(selectedIdea.category)}`}>
                          {selectedIdea.category}
                        </span>
                      </div>

                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">Submitted</h5>
                        <div className="flex items-center text-gray-600">
                          <CalendarIcon className="h-5 w-5 mr-2" />
                          {format(new Date(selectedIdea.dateSubmitted), 'MMMM d, yyyy')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Description</h5>
                    <p className="text-gray-600 leading-relaxed">{selectedIdea.description}</p>
                    
                    {/* AI Analysis Buttons */}
                    {!isAnalyzing && !analysisResult && !analysisError && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          onClick={() => handleStandardAnalysis(selectedIdea)}
                          disabled={isAnalyzing}
                          className="flex items-center px-4 py-2 rounded-full text-sm font-medium text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            background: 'linear-gradient(135deg, #FF6B6B 0%, #8B5CF6 100%)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }}
                        >
                          <SparklesIcon className="h-4 w-4 mr-2" />
                          A.I. Analysis
                        </button>
                        <button
                          onClick={() => handleDeepResearch(selectedIdea)}
                          disabled={isAnalyzing}
                          className="flex items-center px-4 py-2 rounded-full text-sm font-medium text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            background: 'linear-gradient(135deg, #FF6B6B 0%, #8B5CF6 100%)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }}
                        >
                          <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                          Deep Research
                        </button>
                      </div>
                    )}

                    {/* AI Analysis Results - Right where the button was */}
                    {(analysisResult || analysisError || isAnalyzing) && (
                      <div className="mt-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          {isAnalyzing && (
                            <div className="flex flex-col items-center justify-center py-8">
                              <div className="relative mb-4">
                                <div className="ai-loader"></div>
                                <style>{`
                                  .ai-loader {
                                    width: 15px;
                                    aspect-ratio: 1;
                                    position: relative;
                                  }
                                  .ai-loader::before,
                                  .ai-loader::after {
                                    content: "";
                                    position: absolute;
                                    inset: 0;
                                    border-radius: 50%;
                                  }
                                  .ai-loader::before {
                                    background: #035E6E;
                                    box-shadow: -25px 0 #14333B;
                                    animation: ai-loader-1 1s infinite linear;
                                  }
                                  .ai-loader::after {
                                    background: #B3E331;
                                    transform: rotate(0deg) translateX(25px);
                                    animation: ai-loader-2 1s infinite linear;
                                  }
                                  
                                  @keyframes ai-loader-1 {
                                    100% { transform: translateX(25px) }
                                  }
                                  @keyframes ai-loader-2 {
                                    100% { transform: rotate(-180deg) translateX(25px) }
                                  }
                                `}</style>
                              </div>
                              <div className="text-center">
                                <div className="text-sm font-medium text-emerald-900 mb-1">
                                  {analysisType === 'standard' ? 'Analyzing with A.I....' : 'Conducting Deep Research....'}
                                </div>
                                <div className="text-xs text-emerald-700">
                                  {analysisType === 'standard' ? 
                                    'Basic Analysis May Take Up to 3 Minutes' : 
                                    'Deep Research May Take 3-5 Minutes'
                                  }
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {analysisError && (
                            <div className="text-center py-8">
                              <div className="text-red-600 text-sm mb-4">
                                {analysisError}
                              </div>
                              <div className="flex gap-2 justify-center">
                                <button
                                  onClick={() => handleStandardAnalysis(selectedIdea)}
                                  className="flex items-center px-4 py-2 rounded-full text-sm font-medium text-white transition-all duration-200"
                                  style={{
                                    background: 'linear-gradient(135deg, #FF6B6B 0%, #8B5CF6 100%)',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                  }}
                                >
                                  <SparklesIcon className="h-4 w-4 mr-2" />
                                  A.I. Analysis
                                </button>
                                <button
                                  onClick={() => handleDeepResearch(selectedIdea)}
                                  className="flex items-center px-4 py-2 rounded-full text-sm font-medium text-white transition-all duration-200"
                                  style={{
                                    background: 'linear-gradient(135deg, #035E6E 0%, #14333B 100%)',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                  }}
                                >
                                  <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                                  Deep Research
                                </button>
                              </div>
                            </div>
                          )}
                          
                          {analysisResult && (
                            <div>
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-2">
                                  <h5 className="text-sm font-semibold text-gray-700">Results</h5>
                                  <button
                                    onClick={() => setIsAnalysisModalOpen(true)}
                                    className="flex items-center px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-200 transition-colors"
                                    title="View in popup window"
                                  >
                                    <ArrowTopRightOnSquareIcon className="h-3 w-3 mr-1" />
                                    Popup
                                  </button>
                                </div>
                                <button
                                  onClick={handleSaveAnalysis}
                                  disabled={isSavingAnalysis}
                                  className="flex items-center px-3 py-1 bg-deep-teal text-white rounded-lg text-xs font-medium hover:bg-deep-teal-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <DocumentTextIcon className="h-3 w-3 mr-1" />
                                  {isSavingAnalysis ? 'Saving...' : 'Save as Attachment'}
                                </button>
                              </div>
                              <div 
                                className="prose prose-sm max-w-none text-gray-700 bg-white rounded-lg p-4 border border-gray-200 max-h-96 overflow-y-auto"
                                dangerouslySetInnerHTML={{ __html: formatAnalysisText(analysisResult) }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Attachments Section */}
                  <div>
                    <AttachmentList
                      ideaId={selectedIdea.id}
                      className="bg-gray-50 rounded-lg p-4"
                    />
                  </div>

                  <div>
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Owner</h5>
                    <div className="flex items-center bg-gray-50 rounded-lg p-3">
                      <span className="text-2xl mr-3">{selectedIdea.owner.avatar}</span>
                      <div>
                        <div className="font-medium text-gray-900">{selectedIdea.owner.name}</div>
                        <div className="text-sm text-gray-500">Idea Owner</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Priority Score</h5>
                    <div className="flex items-center bg-deep-teal bg-opacity-10 rounded-lg p-4">
                      <StarIcon className="h-6 w-6 text-electric-lime mr-3" />
                      <div>
                        <div className="text-2xl font-bold text-deep-teal">
                          {selectedIdea.score?.toFixed(2) || '0.00'}
                        </div>
                        <div className="text-sm text-gray-600">Priority Score</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Effort & Value</h5>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-red-600">{selectedIdea.effort}</div>
                        <div className="text-sm text-gray-600">Effort (1-10)</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-green-600">{selectedIdea.value}</div>
                        <div className="text-sm text-gray-600">Value (1-10)</div>
                      </div>
                    </div>
                  </div>



                  {/* Comments Section */}
                  {!showLinkedTasks && (
                    <div>
                      <CommentList
                        ideaId={selectedIdea.id}
                        title="Comments"
                        className="bg-gray-50 rounded-lg p-4"
                        refreshTrigger={commentRefreshTrigger}
                      />
                      
                      {showCommentForm && (
                        <div className="mt-4">
                          <CommentForm
                            ideaId={selectedIdea.id}
                            onCommentAdded={handleCommentAdded}
                            onCancel={handleCancelComment}
                            placeholder="Share your thoughts about this idea..."
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Linked Tasks Section */}
                  {showLinkedTasks && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-sm font-semibold text-gray-700">Linked Tasks</h5>
                        <button
                          onClick={() => setShowLinkedTasks(false)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <ArrowLeftIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        {(() => {
                          const linkedTasks = getLinkedTasks(selectedIdea.id);
                          
                          if (linkedTasks.length === 0) {
                            return (
                              <div className="text-center py-8 text-gray-500">
                                <div className="text-4xl mb-3">📝</div>
                                <p className="text-sm mb-4">No tasks linked to this idea yet.</p>
                                <p className="text-xs text-gray-400">Create tasks from the Task Management page to track implementation.</p>
                              </div>
                            );
                          }

                          return (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                                <span>{linkedTasks.length} task{linkedTasks.length !== 1 ? 's' : ''} linked</span>
                                <div className="flex items-center space-x-4 text-xs">
                                  <span className="flex items-center">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-1"></div>
                                    To Do: {linkedTasks.filter(t => t.status === 'To Do').length}
                                  </span>
                                  <span className="flex items-center">
                                    <div className="w-2 h-2 bg-purple-500 rounded-full mr-1"></div>
                                    In Progress: {linkedTasks.filter(t => t.status === 'In Progress').length}
                                  </span>
                                  <span className="flex items-center">
                                    <div className="w-2 h-2 bg-emerald-500 rounded-full mr-1"></div>
                                    Done: {linkedTasks.filter(t => t.status === 'Done').length}
                                  </span>
                                </div>
                              </div>
                              
                              {linkedTasks.map((task) => (
                                <div key={task.id} className="bg-white rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors">
                                  <div className="flex items-start justify-between mb-2">
                                    <h6 className="font-medium text-gray-900 text-sm">{task.title}</h6>
                                    <div className="flex items-center space-x-2 ml-3">
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTaskStatusColor(task.status)}`}>
                                        {task.status}
                                      </span>
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTaskPriorityColor(task.priority)}`}>
                                        {task.priority === 'High' ? '🔴' : task.priority === 'Medium' ? '🟡' : '🟢'} {task.priority}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {task.description && (
                                    <p className="text-xs text-gray-600 mb-3 line-clamp-2">{task.description}</p>
                                  )}
                                  
                                  <div className="flex items-center justify-between text-xs text-gray-500">
                                    <div className="flex items-center space-x-3">
                                      {task.assignee && (
                                        <div className="flex items-center">
                                          <UserIcon className="h-3 w-3 mr-1" />
                                          <span>{task.assignee.name}</span>
                                        </div>
                                      )}
                                      {task.dueDate && (
                                        <div className="flex items-center">
                                          <ClockIcon className="h-3 w-3 mr-1" />
                                          <span>{format(new Date(task.dueDate), 'MMM d')}</span>
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-gray-400">
                                      Created {format(new Date(task.dateCreated), 'MMM d')}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}


                </div>
              </div>
              
              {/* Footer Actions */}
              <div className="border-t border-gray-200 px-6 py-4">
                {showLinkedTasks ? (
                  /* Task View Actions */
                  <div className="space-y-3">
                    <div className="flex space-x-3">
                      <button 
                        onClick={() => setShowLinkedTasks(false)}
                        className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        <ArrowLeftIcon className="h-4 w-4 inline mr-2" />
                        Back to Idea
                      </button>
                      <button className="flex-1 bg-deep-teal text-white px-4 py-2 rounded-lg hover:bg-deep-teal/90 transition-colors">
                        <PlusIcon className="h-4 w-4 inline mr-2" />
                        Create Task
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Idea View Actions */
                  <div className="space-y-3">
                    <div className="flex space-x-3">
                      {canEditIdea(selectedIdea) ? (
                        <button 
                          onClick={handleEditIdea}
                          className="flex-1 bg-deep-teal text-white px-4 py-2 rounded-lg hover:bg-deep-teal/90 transition-colors"
                        >
                          <PencilIcon className="h-4 w-4 inline mr-2" />
                          Edit Idea
                        </button>
                      ) : (
                        <button 
                          className="flex-1 bg-gray-400 text-white px-4 py-2 rounded-lg cursor-not-allowed"
                          disabled
                          title="You can only edit your own ideas"
                        >
                          <PencilIcon className="h-4 w-4 inline mr-2" />
                          Edit Idea
                        </button>
                      )}
                      <button 
                        onClick={handleViewTasks}
                        className="flex-1 bg-deep-teal text-white px-4 py-2 rounded-lg hover:bg-deep-teal/90 transition-colors"
                      >
                        View Tasks ({getLinkedTasks(selectedIdea.id).length})
                      </button>
                    </div>
                    <div className="flex space-x-3">
                      <button 
                        onClick={handleAddComment}
                        className="flex-1 bg-deep-teal text-white px-4 py-2 rounded-lg hover:bg-deep-teal/90 transition-colors"
                      >
                        <ChatBubbleLeftIcon className="h-4 w-4 inline mr-2" />
                        Add Comment
                      </button>
                      <button className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors">
                        <TrashIcon className="h-4 w-4 inline mr-2" />
                        Delete Idea
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* AI Analysis Modal */}
      {isAnalysisModalOpen && analysisResult && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsAnalysisModalOpen(false);
            }
          }}
        >
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <SparklesIcon className="h-6 w-6 text-purple-600" />
                <h2 className="text-xl font-bold text-gray-900">
                  {analysisType === 'standard' ? 'A.I. Analysis Results' : 'Deep Research Results'}
                </h2>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleSaveAnalysis}
                  disabled={isSavingAnalysis}
                  className="flex items-center px-3 py-2 bg-deep-teal text-white rounded-lg text-sm font-medium hover:bg-deep-teal-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  {isSavingAnalysis ? 'Saving...' : 'Save as Attachment'}
                </button>
                <button
                  onClick={() => setIsAnalysisModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div 
                className="prose prose-lg max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: formatAnalysisText(analysisResult) }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IdeasManagement; 